import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorPayload } from "../../../interfaces/auth/IErrorPayload";
import Button from "../../../shared/button";
import Loading from "../../../shared/Loading";
import { useSignUpMutation } from "../../../store/auth/authApi";
import { ISignUpUser } from "../../../interfaces/auth/ISignUpUser";
import SignupStep1 from "./SignUpStep1";
import SignupStep2 from "./SignUpStep2";
import { classNames } from "../../../utils/classNames";
import { NavLink } from "react-router-dom";

const SignUpForm = () => {
  const [isLastStep, setIsLastStep] = useState<boolean>(false);
  const [successPage, setSuccessPage] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    trigger,
  } = useForm<ISignUpUser>();
  const [signUpMutation, { isLoading, error }] = useSignUpMutation();
  let errorMsg: string;

  const onSubmit = async (payload: ISignUpUser) => {
    try {
      const { data } = await signUpMutation({
        ...payload,
        name: payload.fullName,
        dashboard: "hoteworld",
        hotelDetails: {
          hotelType: payload.singleHotel,
          hotelEmail: payload.hotelEmail,
          hotelPhoneNumber: payload.hotelPhoneNumber,
          hotelAddress: {
            street: payload.hotelAddress,
            city: payload.hotelAddress,
          },
          hotelName: payload.hotelName,
        },
        company: {
          name: payload.hotelName,
          address: payload.hotelAddress,
          country: payload.country,
        },
        userType: "organization-account",
      }).unwrap();
      setIsLastStep(false);
      setSuccessPage(true);
    } catch (err: any) {
      errorMsg = err?.data?.message;
    }
  };

  const handleNextStep = async () => {
    if (
      await trigger([
        "fullName",
        "email",
        "phoneNumber",
        "jobTitle",
        "hotelName",
        "country",
        "singleHotel",
      ])
    )
      setIsLastStep(true);
  };

  const handleResetStep = async () => setSuccessPage(false);

  const err = { ...(error as ErrorPayload) };
  errorMsg = "Something went wrong";
  return (
    <>
      <form
        className="mt-8 mb-5 w-full md:w-6/12 mx-auto"
        onSubmit={handleSubmit(onSubmit)}
      >
        {!!err.data && (
          <div
            className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
            role="alert"
          >
            <span className="font-medium">
              {" "}
              {err.data.message || errorMsg}{" "}
            </span>
          </div>
        )}
        {successPage ? (
          <>
            <h2 className="text-lg md:text-2xl font-semibold mb-2">
              Successfully signed up
            </h2>
            <p className="text-base font-normal">
              You can continue to your dashboard.!
            </p>
            <img src="/gif/success.gif" alt="" />
            <div className="w-full text-center">
              <NavLink
                to="login"
                className="w-full bg-saloon-btn hover:bg-opacity-80 p-3 text-saloon-primary rounded-3xl font-semibold text-base mt-8 mx-auto"
                type="button"
              >
                Next
              </NavLink>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg md:text-2xl font-semibold mb-2">Sign up</h2>
            <div className={isLastStep ? "hidden" : "block"}>
              <SignupStep1 register={register} errors={errors} />
            </div>
            {isLastStep && (
              <div>
                <SignupStep2
                  register={register}
                  errors={errors}
                  password={getValues("password")}
                />
              </div>
            )}
            <Button
              className={classNames(
                "w-full bg-saloon-btn hover:bg-opacity-80 p-3 text-saloon-primary rounded-3xl font-semibold text-base mt-8",
                isLastStep ? "block" : "hidden"
              )}
              type="submit"
              label={
                <>
                  {isLoading && <Loading />}
                  Next
                </>
              }
            />
            {!isLastStep && (
              <Button
                className="w-full bg-saloon-btn hover:bg-opacity-80 p-3 text-saloon-primary rounded-3xl font-semibold text-base mt-8"
                type="button"
                onClickButton={handleNextStep}
                label="Next"
              />
            )}
          </>
        )}
      </form>
    </>
  );
};

export default SignUpForm;
